import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Space, Spin, Typography } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import message from "@/utils/globalMessage";
import { api } from "@/redux/api";
import ResponsiveSelect from "@components/ResponsiveSelect";
import { SERVICE_REQUEST_PRIORITIES } from "@/containers/service-request/constants";
import {
  CreateHeader,
  CreatePage,
  FormActions,
  FormCard,
  FormPanel,
  ProgressLabel,
  ReviewGrid,
  ReviewItem,
  StepButton,
  StepHeading,
  StepList,
  StepNumber,
  StepsPanel,
  StepText,
  WizardLayout,
} from "./TenantServiceRequestCreate.styles";

const WIZARD_STEPS = [
  { title: "Issue Details", fields: ["title", "priority", "description"] },
  { title: "Location & Category", fields: ["facility", "suite_area", "category"] },
  { title: "Review & Submit", fields: [] },
];

const PRIORITY_LABELS = {
  Low: "Low - Not urgent",
  Important: "Important - Needs attention",
  Urgent: "Urgent - Critical issue",
};

const getResponseRows = (response) => {
  const payload = response?.data;
  return Array.isArray(payload) ? payload : payload?.results || [];
};

export default function TenantServiceRequestCreatePage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const listPath = "/tennant/service-request";

  useEffect(() => {
    let active = true;

    Promise.all([api.getTenantFacilities(), api.getTenantServiceCategories()])
      .then(([facilityResponse, categoryResponse]) => {
        if (!active) return;

        const facilityRows = getResponseRows(facilityResponse);
        const categoryRows = getResponseRows(categoryResponse);
        setFacilities(facilityRows);
        setCategories(categoryRows);

        const facilityId = facilityRows.length === 1 ? facilityRows[0].id : undefined;
        form.setFieldsValue({ facility: facilityId, priority: "Low" });
      })
      .catch(() => {
        if (active) message.error("Failed to load request options");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [form]);

  const submit = async (values) => {
    setSubmitting(true);
    try {
      const response = await api.createTenantServiceRequest(values);
      message.success("Service request created successfully");
      navigate(`/tennant/service-request-detail/${response.data.id}`, { replace: true });
    } catch (error) {
      message.error("Failed to create service request");
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    try {
      await form.validateFields(WIZARD_STEPS[currentStep].fields);
      setCurrentStep((step) => Math.min(step + 1, WIZARD_STEPS.length - 1));
    } catch (error) {
      // Ant Design renders field-level validation messages.
    }
  };

  const goBack = () => {
    if (currentStep === 0) {
      navigate(listPath);
      return;
    }
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const reviewValues = currentStep === 2 ? form.getFieldsValue(true) : {};
  const facilityName = useMemo(() => {
    const facility = facilities.find((item) => String(item.id) === String(reviewValues.facility));
    return facility?.name || "--";
  }, [facilities, reviewValues.facility]);
  const categoryName = useMemo(() => {
    const category = categories.find((item) => String(item.id) === String(reviewValues.category));
    return category?.name || "--";
  }, [categories, reviewValues.category]);

  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <>
          <StepHeading>
            <h2>Step 1: Issue Details</h2>
          </StepHeading>
          <Form.Item
            label="Title / Issue"
            name="title"
            rules={[
              { required: true, message: "Please enter a title / issue" },
              { whitespace: true, message: "Please enter a title / issue" },
            ]}
          >
            <Input maxLength={255} placeholder="e.g. AC not cooling in Suite 301" />
          </Form.Item>
          <Form.Item
            label="Priority"
            name="priority"
            rules={[{ required: true, message: "Please select a priority" }]}
          >
            <ResponsiveSelect
              placeholder="Select a priority"
              popupTitle="Select Priority"
              options={SERVICE_REQUEST_PRIORITIES.map((value) => ({
                value,
                label: PRIORITY_LABELS[value] || value,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please enter a description" },
              { whitespace: true, message: "Please enter a description" },
            ]}
          >
            <Input.TextArea
              rows={6}
              placeholder="Describe the issue, when it started, and any relevant context"
            />
          </Form.Item>
        </>
      );
    }

    if (currentStep === 1) {
      return (
        <>
          <StepHeading>
            <h2>Step 2: Location &amp; Category</h2>
          </StepHeading>
          <Form.Item
            label="Facility"
            name="facility"
            rules={[{ required: true, message: "Please select a facility" }]}
          >
            <ResponsiveSelect
              placeholder="Select a facility"
              popupTitle="Select Facility"
              showSearch
              options={facilities.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item
            label="Suite / Area"
            name="suite_area"
            rules={[
              { required: true, message: "Please enter Suite / Area" },
              { whitespace: true, message: "Please enter Suite / Area" },
            ]}
          >
            <Input maxLength={255} placeholder="e.g. Suite 301 or West Lobby" />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <ResponsiveSelect
              placeholder="Select a category"
              popupTitle="Select Category"
              showSearch
              options={categories.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
        </>
      );
    }

    return (
      <>
        <StepHeading>
          <h2>Step 3: Review &amp; Submit</h2>
        </StepHeading>
        <ReviewGrid as="dl">
          <ReviewItem>
            <dt>Title / Issue</dt>
            <dd>{reviewValues.title || "--"}</dd>
          </ReviewItem>
          <ReviewItem>
            <dt>Priority</dt>
            <dd>{reviewValues.priority || "--"}</dd>
          </ReviewItem>
          <ReviewItem $full>
            <dt>Description</dt>
            <dd>{reviewValues.description || "--"}</dd>
          </ReviewItem>
          <ReviewItem>
            <dt>Facility</dt>
            <dd>{facilityName}</dd>
          </ReviewItem>
          <ReviewItem>
            <dt>Suite / Area</dt>
            <dd>{reviewValues.suite_area || "--"}</dd>
          </ReviewItem>
          <ReviewItem>
            <dt>Category</dt>
            <dd>{categoryName}</dd>
          </ReviewItem>
        </ReviewGrid>
      </>
    );
  };

  return (
    <CreatePage>
      {isPC ? (
        <CreateHeader>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(listPath)}
            title="Back"
            aria-label="Back"
          />
          <Typography.Title level={4}>
            Create Service Request
          </Typography.Title>
        </CreateHeader>
      ) : null}

      <Spin spinning={loading}>
        <WizardLayout>
          <StepsPanel>
            <ProgressLabel>Progress</ProgressLabel>
            <StepList>
              {WIZARD_STEPS.map((step, index) => {
                const active = index === currentStep;
                const completed = index < currentStep;
                return (
                  <StepButton
                    key={step.title}
                    type="button"
                    $active={active}
                    $clickable={completed}
                    disabled={!completed}
                    onClick={() => completed && setCurrentStep(index)}
                    aria-current={active ? "step" : undefined}
                    aria-label={`Step ${index + 1}: ${step.title}`}
                  >
                    <StepNumber $active={active || completed}>
                      {completed ? <CheckOutlined /> : index + 1}
                    </StepNumber>
                    <StepText>{step.title}</StepText>
                  </StepButton>
                );
              })}
            </StepList>
          </StepsPanel>

          <FormPanel>
            <FormCard>
              <Form
                form={form}
                layout="vertical"
                onFinish={submit}
                autoComplete="off"
                preserve
              >
                {renderStepContent()}
                <FormActions>
                  <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
                    {currentStep === 0 ? "Cancel" : "Previous"}
                  </Button>
                  {currentStep < WIZARD_STEPS.length - 1 ? (
                    <Button type="primary" icon={<ArrowRightOutlined />} onClick={goNext}>
                      Next
                    </Button>
                  ) : (
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                      >
                        Create Request
                      </Button>
                    </Space>
                  )}
                </FormActions>
              </Form>
            </FormCard>
          </FormPanel>
        </WizardLayout>
      </Spin>
    </CreatePage>
  );
}
